import Button from '@orn/shared-web/components/Button/Button';
import { OTPInput } from '@orn/shared-web/components/Input';
import * as React from 'react';

import useAuthApi from '../../../hooks/useAuthApi';
import useCountdown from '../../../hooks/useCountdown';
import LoginHeader from '../LoginHeader';
import LoginTitle from '../LoginTitle';
import { RESEND_COUNTDOWN_SECONDS } from '../types';

import type { OtpChannel, OtpPurpose } from '../types';

interface IOTPVerificationStepProps {
  email: string;
  userId: string;
  otpPurpose: OtpPurpose;
  otpChannel?: OtpChannel;
  phoneLast4?: string;
  onSuccess: () => void;
  onWaitlisted: () => void;
  onBack: () => void;
}

const OTPVerificationStep = ({
  email,
  userId,
  otpPurpose,
  otpChannel = 'email',
  phoneLast4: initialPhoneLast4,
  onSuccess,
  onWaitlisted,
  onBack,
}: IOTPVerificationStepProps) => {
  const authApi = useAuthApi();
  const countdown = useCountdown();
  const [otpValue, setOtpValue] = React.useState('');
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [errorMessage, setErrorMessage] = React.useState<string | undefined>();
  const [currentChannel, setCurrentChannel] = React.useState<OtpChannel>(otpChannel);
  const [phoneLast4, setPhoneLast4] = React.useState(initialPhoneLast4);

  // Start countdown on mount (code was just sent)
  React.useEffect(() => {
    countdown.start(RESEND_COUNTDOWN_SECONDS);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleVerify = React.useCallback(
    async (code: string) => {
      setIsSubmitting(true);
      setErrorMessage(undefined);

      const verifyFn = otpPurpose === 'registration' ? authApi.verifyRegistrationOtp : authApi.verifyLoginOtp;
      const result = await verifyFn({ userId, code });

      setIsSubmitting(false);

      if (!result.ok) {
        if (result.error.code === 'AUTH_NOT_ON_ALPHA_ALLOW_LIST') {
          onWaitlisted();
          return;
        }
        setErrorMessage(
          result.error.code === 'AUTH_INVALID_OTP' ? 'Invalid or expired code. Please try again.' : result.error.error
        );
        setOtpValue('');
        return;
      }

      onSuccess();
    },
    [authApi, otpPurpose, userId, onSuccess, onWaitlisted]
  );

  const handleResend = React.useCallback(
    async (channel?: OtpChannel) => {
      setErrorMessage(undefined);

      if (otpPurpose === 'registration') {
        const result = await authApi.resendRegistrationOtp({ userId });
        if (!result.ok) {
          setErrorMessage(
            result.error.code === 'AUTH_RESEND_LIMIT'
              ? 'Too many resend attempts. Please wait before trying again.'
              : result.error.error
          );
          return;
        }
      } else {
        const result = await authApi.resendLoginOtp({ userId, channel });
        if (!result.ok) {
          const msg =
            result.error.code === 'AUTH_RESEND_LIMIT' || result.error.code === 'AUTH_SMS_RATE_LIMITED'
              ? 'Too many resend attempts. Please wait before trying again.'
              : result.error.error;
          setErrorMessage(msg);
          return;
        }
        if (result.data.otpChannel) {
          setCurrentChannel(result.data.otpChannel);
          setPhoneLast4(result.data.phoneLast4);
        }
      }

      countdown.start(RESEND_COUNTDOWN_SECONDS);
    },
    [authApi, otpPurpose, userId, countdown]
  );

  const handleComplete = React.useCallback(
    (code: string) => {
      void handleVerify(code);
    },
    [handleVerify]
  );

  const isLoginSms = otpPurpose === 'login' && currentChannel === 'sms';
  const title = currentChannel === 'sms' ? 'Check your phone' : 'Check your email';
  const smsSubtitle = phoneLast4
    ? `We sent a code to your phone ending in ${phoneLast4}`
    : 'We sent a code to your phone';
  const subtitle = currentChannel === 'sms' ? smsSubtitle : `We sent a code to ${email}`;

  return (
    <>
      <LoginHeader onBack={onBack} />
      <LoginTitle title={title} subtitle={subtitle} />

      <div className="flex w-full flex-col items-center gap-6">
        <OTPInput
          value={otpValue}
          onChange={setOtpValue}
          onComplete={handleComplete}
          isInvalid={Boolean(errorMessage)}
          errorMessage={errorMessage}
          isDisabled={isSubmitting}
          autoFocus
        />

        <div className="flex flex-col items-center gap-1">
          {countdown.isActive ? (
            <span className="text-tertiary text-sm">Resend code in {countdown.secondsRemaining}s</span>
          ) : (
            <>
              <Button color="link-color" size="sm" onClick={() => void handleResend()}>
                {isLoginSms ? 'Resend code via SMS' : 'Resend code'}
              </Button>
              {isLoginSms && (
                <Button color="link-color" size="sm" onClick={() => void handleResend('email')}>
                  Send via email instead
                </Button>
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
};

OTPVerificationStep.displayName = 'OTPVerificationStep';

export default OTPVerificationStep;
